// "Envoyer" — pick files, get a link + QR code, follow every recipient (FilePizza mode).

import { Mesh } from '../core/mesh';
import { deriveAuthKey } from '../core/auth';
import { randomSecret, roomIdFor } from '../core/ids';
import { DropSender, type ReceiverState } from '../drop/sender';
import { formatBytes } from '../drop/protocol';
import { h, keep, progressBar, type Maybe } from './dom';
import { copyText, deviceName, iceServers, keepAwake, qrElement, wsUrl } from './common';
import type { View } from './view';

const STATUS: Record<ReceiverState['status'], string> = {
  connecting: 'connexion…',
  offered: 'proposé',
  ready: 'connecté',
  receiving: 'téléchargement',
  done: 'terminé',
  declined: 'refusé',
  refused: 'mot de passe incorrect',
  gone: 'parti',
};

export function sendView(root: HTMLElement): View {
  let files: File[] = [];
  let password = '';
  let share: { mesh: Mesh; sender: DropSender; link: string; qr: HTMLElement; release: () => void } | null = null;
  let error: string | null = null;
  let frame = 0;

  const input = h('input', { type: 'file', multiple: true, hidden: true, onchange: () => addFiles([...(input.files ?? [])]) });

  function addFiles(list: File[]) {
    files = [...files, ...list];
    input.value = '';
    render();
  }

  async function startSharing() {
    error = null;
    try {
      const secret = randomSecret();
      const mesh = new Mesh({
        url: wsUrl(`/ws/room/${await roomIdFor(secret)}`),
        iceServers: await iceServers(),
        authKey: await deriveAuthKey(secret, password),
      });
      await mesh.start();
      const sender = new DropSender(mesh, files, { from: deviceName(), mode: 'link' });
      sender.addEventListener('change', schedule);
      sender.start();
      const link = `${location.origin}/#/r/${secret}${password ? '/p' : ''}`;
      share = { mesh, sender, link, qr: qrElement(link), release: keepAwake() };
    } catch (e) {
      error = `Impossible de démarrer le partage : ${(e as Error).message}`;
    }
    render();
  }

  function stopSharing() {
    if (!share) return;
    share.sender.stop();
    share.mesh.close();
    share.release();
    share = null;
    render();
  }

  function schedule() {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  }

  function pickScreen(): Maybe[] {
    const total = files.reduce((n, f) => n + f.size, 0);
    const zone = h(
      'label',
      {
        class: 'drop-zone',
        ondragover: (e: DragEvent) => e.preventDefault(),
        ondrop: (e: DragEvent) => {
          e.preventDefault();
          addFiles([...(e.dataTransfer?.files ?? [])]);
        },
      },
      input,
      h('span', { class: 'drop-zone-title', text: files.length ? '+ Ajouter des fichiers' : 'Choisir des fichiers' }),
      h('span', { class: 'dim', text: 'ou les déposer ici' }),
    );
    return [
      h('h1', { text: 'Envoyer des fichiers' }),
      h('p', { class: 'dim', text: 'Directement d’appareil à appareil, chiffré de bout en bout. Rien n’est stocké sur un serveur.' }),
      zone,
      files.length > 0 &&
        h(
          'ul',
          { class: 'files' },
          ...files.map((f, i) =>
            h(
              'li',
              { class: 'file' },
              h(
                'div',
                { class: 'file-head' },
                h('span', { class: 'file-name', text: f.name }),
                h('span', { class: 'dim', text: formatBytes(f.size) }),
                h('button', { class: 'icon-btn', 'aria-label': `Retirer ${f.name}`, text: '×', onclick: () => ((files = files.filter((_, j) => j !== i)), render()) }),
              ),
            ),
          ),
        ),
      files.length > 0 &&
        h(
          'details',
          null,
          h('summary', { text: 'Mot de passe (facultatif)' }),
          h('input', {
            type: 'password',
            class: 'field',
            autocomplete: 'new-password',
            placeholder: 'à transmettre séparément',
            value: password,
            oninput: (e: Event) => (password = (e.target as HTMLInputElement).value),
          }),
        ),
      error ? h('p', { class: 'bad', text: error }) : null,
      h('button', {
        class: 'btn primary',
        disabled: files.length === 0,
        text: files.length ? `Créer le lien (${files.length} · ${formatBytes(total)})` : 'Créer le lien',
        onclick: () => void startSharing(),
      }),
    ];
  }

  function shareScreen(s: NonNullable<typeof share>): Maybe[] {
    const receivers = [...s.sender.receivers.values()];
    const copyBtn = h('button', {
      class: 'btn small',
      text: 'Copier',
      onclick: async () => (copyBtn.textContent = (await copyText(s.link)) ? 'Copié' : 'Copie impossible'),
    });
    return [
      h('h1', { text: 'Partage en cours' }),
      h('p', { class: 'warn', text: 'Garde cette page ouverte jusqu’à la fin des téléchargements.' }),
      h('div', { class: 'row' }, h('input', { class: 'field grow', readonly: true, value: s.link, onfocus: (e: Event) => (e.target as HTMLInputElement).select() }), copyBtn),
      'share' in navigator && h('button', { class: 'btn', text: 'Partager le lien…', onclick: () => void navigator.share({ url: s.link }).catch(() => {}) }),
      s.qr,
      password ? h('p', { class: 'dim', text: 'Protégé par mot de passe : transmets-le par un autre canal.' }) : null,
      h('p', { class: 'dim', text: `${s.sender.metas.length} fichier${s.sender.metas.length > 1 ? 's' : ''} · ${formatBytes(s.sender.totalSize)}` }),
      h('h2', { text: 'Destinataires' }),
      receivers.length === 0
        ? h('p', { class: 'dim', text: 'Personne pour l’instant. Envoie le lien ou fais scanner le QR code.' })
        : h(
            'ul',
            { class: 'files' },
            ...receivers.map((r) => {
              const bytes = s.sender.bytesFor(r);
              return h(
                'li',
                { class: 'file' },
                h('div', { class: 'file-head' }, h('span', { class: 'file-name', text: r.name }), h('span', { class: r.status === 'done' ? 'ok' : r.status === 'refused' ? 'bad' : 'dim', text: STATUS[r.status] })),
                (r.status === 'receiving' || r.status === 'done') && progressBar(bytes / Math.max(1, s.sender.totalSize)),
                r.status === 'receiving' && h('div', { class: 'dim', text: `${formatBytes(bytes)} / ${formatBytes(s.sender.totalSize)}` }),
              );
            }),
          ),
      h('button', { class: 'btn danger', text: 'Arrêter le partage', onclick: stopSharing }),
    ];
  }

  function render() {
    root.replaceChildren(...keep(...(share ? shareScreen(share) : pickScreen())));
  }

  const beforeUnload = (e: BeforeUnloadEvent) => {
    if (share) e.preventDefault();
  };
  addEventListener('beforeunload', beforeUnload);
  render();

  return {
    busy: () => !!share,
    destroy() {
      cancelAnimationFrame(frame);
      removeEventListener('beforeunload', beforeUnload);
      stopSharing();
    },
  };
}
